import { flatten } from 'lodash-es';
import { useTableConditionalFormatter } from '~/bi/components/chart-builder/tabs/table-tabs/conditional-formatting/composables/bi-conditional-formatter.composable.js';
import { useBiTableHelper } from '~/bi/components/widgets/table-widget/composables/bi-table-helper.composables.js';

export function useBiMapHelper() {
  const HEATMAP_LAYER_ID = 'all-features-heatmap';
  const CLUSTER_LAYER_IDS = ['clusters', 'cluster-count', 'unclustered-point', 'unclustered-point-label'];
  const AVAILABLE_ICONS = [
    { name: 'asset', key: 'asset', path: '/img/icons/asset.png' },

    // Colored icons
    { name: 'defects-2ED3B7', key: 'defects-2ED3B7', path: '/img/icons/defects/2ED3B7.png' },
    { name: 'defects-1570EF', key: 'defects-1570EF', path: '/img/icons/defects/1570EF.png' },
    { name: 'defects-6938EF', key: 'defects-6938EF', path: '/img/icons/defects/6938EF.png' },
    { name: 'defects-039855', key: 'defects-039855', path: '/img/icons/defects/039855.png' },
    { name: 'defects-475467', key: 'defects-475467', path: '/img/icons/defects/475467.png' },
    { name: 'defects-D92D20', key: 'defects-D92D20', path: '/img/icons/defects/D92D20.png' },
    { name: 'defects-D444F1', key: 'defects-D444F1', path: '/img/icons/defects/D444F1.png' },
    { name: 'defects-DC6803', key: 'defects-DC6803', path: '/img/icons/defects/DC6803.png' },
    { name: 'defects-EE46BC', key: 'defects-EE46BC', path: '/img/icons/defects/EE46BC.png' },
    { name: 'defects-FF692E', key: 'defects-FF692E', path: '/img/icons/defects/FF692E.png' },
  ];
  const DEFAULT_HEATMAP_COLORS = [
    'rgb(103,169,207)',
    'rgb(209,229,240)',
    'rgb(253,219,199)',
    'rgb(239,138,98)',
    'rgb(178,24,43)',
  ];

  function getSourcesLayers() {
    // Mapbox sources used by the map
    const sources = ['all_features_source', 'symbol-source'];

    // Mapbox layers configuration
    const layers = [
      // POINT FEATURES (now visible)
      {
        id: 'point_feature_layer',
        type: 'circle',
        source: 'all_features_source',
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': ['coalesce', ['get', 'feature_point_size'], 6],
          'circle-color': ['coalesce', ['get', 'feature_color'], '#ff3b3b'],
          'circle-opacity': 0.9,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff',
        },
      },

      // POLYGON FEATURES
      {
        id: 'polygon_feature_layer',
        type: 'fill',
        source: 'all_features_source',
        filter: ['==', '$type', 'Polygon'],
        paint: {
          'fill-opacity': 0, // invisible but still interactive
        },
      },

      // LINESTRING FEATURES
      {
        id: 'linestring_feature_layer',
        type: 'line',
        source: 'all_features_source',
        filter: ['==', '$type', 'LineString'],
        paint: {
          'line-width': 2,
          'line-color': '#333333',
        },
      },

      // SYMBOL ICON LAYER
      {
        id: 'symbol-layer-icon',
        type: 'symbol',
        source: 'symbol-source',
        layout: {
          'icon-image': ['coalesce', ['get', 'feature_icon'], 'asset'],
          'icon-size': ['coalesce', ['get', 'feature_icon_size'], 1],
          'icon-allow-overlap': true,
        },
      },
    ];

    return { sources, layers };
  }
  function loadIcons(map) {
    AVAILABLE_ICONS.forEach((icon) => {
      map.loadImage(icon.path, (error, image) => {
        if (error)
          throw error;
        map.addImage(icon.key, image);
      });
    });
  }

  function handleMouseEvents(layer_id, map) {
    const onMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('mouseenter', layer_id, onMouseEnter);
    map.on('mouseleave', layer_id, onMouseLeave);
  }

  const layerClickHandlers = {};
  let openedPopups = null; ;
  function handleClickEvents(layer_id, map, config = {}) {
    if (layerClickHandlers[layer_id]) {
      map.off('click', layer_id, layerClickHandlers[layer_id]);
    }
    if (openedPopups) {
      openedPopups.remove();
      openedPopups = null;
    }
    const onClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: [layer_id],
      });
      if (features.length > 0) {
        const feature = features[0];

        let popup_html = '';
        if (config?.properties?.length) {
          const properties_content = config.properties.map((key) => {
            return `
          <div>
            <div class="text-xs font-medium mb-0.5">
              ${key}
            </div>
            <div class="text-xs">
            ${feature.properties[key] || '-'}
            </div>
          </div>
          `;
          }).join('');
          popup_html = `<div class="flex flex-col gap-1">${properties_content}</div>`;
        }
        else {
          popup_html = '<div class="text-sm">No properties to display</div>';
        }
        if (config.feature_popup_label) {
          popup_html = `<div class="text-sm font-medium mb-1.5 max-w-[250px]">${feature.properties[config.feature_popup_label] || '-'}</div>${popup_html}`;
        }
        // eslint-disable-next-line no-undef
        openedPopups = new mapboxgl.Popup()
          .setLngLat(feature.geometry.coordinates)
          .setHTML(popup_html)
          .addTo(map);
      }
    };
    layerClickHandlers[layer_id] = onClick;

    map.on('click', layer_id, onClick);
  }

  function getColorStops(config) {
    let final_colors = [...DEFAULT_HEATMAP_COLORS];
    if (config.inverse_colors) {
      final_colors = final_colors.reverse();
    }
    const step = 1 / (final_colors.length);

    const color_stops = final_colors.map((color, index) => [(index + 1) * step, color]);
    return flatten(color_stops);
  }

  function addHeatmapLayer(map, config = {}) {
    // Remove existing heatmap layer if it exists
    if (map.getLayer('all-features-heatmap')) {
      map.removeLayer('all-features-heatmap');
    }

    const color_stops = getColorStops(config);

    const expression = ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(0,0,0,0)'];
    expression.push(...color_stops);

    map.addLayer({
      id: 'all-features-heatmap',
      type: 'heatmap',
      source: 'all_features_source',
      maxzoom: 24,
      paint: {
        'heatmap-weight': [
          'interpolate',
          ['linear'],
          ['get', 'heatmap_value'],
          0,
          0,
          1,
          1,
        ],

        // Increase intensity when zoomed in
        'heatmap-intensity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0,
          1,
          10,
          1.5,
          15,
          2,
        ],

        // Color ramp
        'heatmap-color': expression,

        'heatmap-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0,
          10, // zoomed out → small radius
          5,
          25, // zoom 5 → radius 25px
          10,
          40, // zoom 10 → radius 40px
          15,
          60, // zoom 15 → radius 60px
        ],

        // Transition from heatmap to circle layer by zoom level
        'heatmap-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          7,
          1,
          9,
          0,
        ],

      },
    });

    map.addLayer({
      id: 'point_feature_layer',
      type: 'circle',
      source: 'all_features_source',
      minzoom: 7,
      paint: {
        'circle-radius': 6,
        'circle-color': [
          'interpolate',
          ['linear'],
          ['get', 'heatmap_value'],
          0,
          color_stops[1],
          ...color_stops,
        ],
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 1,
        // Transition from heatmap to circle layer by zoom level
        'circle-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          7,
          0,
          8,
          1,
        ],
      },
    });
  }

  function getClusterCircleColor({ conditional_formatting, feature_value }, cluster_color) {
    const rules = conditional_formatting.filter(
      rule => rule.formatting_style === 'single_color' && rule.field === feature_value,
    );

    if (rules.length) {
      const expression = ['case'];
      const field_name = 'display_value_sum';
      const coalesced_value = ['coalesce', ['get', field_name], 0];

      rules.forEach((rule) => {
        const value = Number(rule.value);

        let condition;
        switch (rule.operator) {
          case 'less_than':
            condition = ['<', coalesced_value, value];
            break;

          case 'less_than_or_equal':
            condition = ['<=', coalesced_value, value];
            break;

          case 'greater_than':
            condition = ['>', coalesced_value, value];
            break;

          case 'greater_than_or_equal':
            condition = ['>=', coalesced_value, value];
            break;

          case 'equal':
            condition = ['==', coalesced_value, value];
            break;

          default:
            return; // skip unsupported operators
        }

        if (condition) {
          expression.push(condition, rule.color);
        }
      });
      expression.push(cluster_color);

      return expression;
    }

    return cluster_color;
  }

  function addClusteringLayers(map, config = {}) {
    const {
      enabled = true,
      show_labels = true,
      cluster_color = '#51bbd6',
      prefix = '',
      suffix = '',
    } = config;

    CLUSTER_LAYER_IDS.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    });

    if (!enabled)
      return;

    const calculated_cluster_color = getClusterCircleColor(config, cluster_color);

    // Add cluster circle layer with radius based on point_count
    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'all_features_source',
      filter: ['has', 'point_count'],
      paint: {
        'circle-radius': [
          'step',
          ['coalesce', ['get', 'display_value_sum'], 0],
          18,
          1000,
          24,
          10000,
          32,
          100000,
          40,
        ],
        'circle-color': calculated_cluster_color,
        'circle-opacity': 0.8,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
    });

    map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'all_features_source',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': [
          'step',
          ['coalesce', ['get', 'display_value_sum'], 0],
          18,
          1000,
          24,
          10000,
          32,
          100000,
          40,
        ],
        'circle-color': ['coalesce', ['get', 'feature_color'], '#ff3b3b'],
        'circle-opacity': 0.9,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
    });

    // Add interactions
    map.on('click', 'clusters', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      const clusterId = features[0].properties.cluster_id;
      map.getSource('all_features_source').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err)
            return;

          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom,
          });
        },
      );
    });
    handleClickEvents('unclustered-point', map, config);
    ['clusters', 'unclustered-point'].forEach((layerId) => {
      handleMouseEvents(layerId, map);
    });

    if (show_labels) {
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'all_features_source',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': [
            'concat',
            prefix,
            [
              'number-format',
              ['get', 'display_value_sum'],
              { 'min-fraction-digits': 0, 'max-fraction-digits': 2 },
            ],
            suffix,
          ],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 13,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      map.addLayer({
        id: 'unclustered-point-label',
        type: 'symbol',
        source: 'all_features_source',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-field': [
            'concat',
            prefix,
            [
              'number-format',
              ['get', 'display_value'],
              { 'min-fraction-digits': 0, 'max-fraction-digits': 2 },
            ],
            suffix,
          ],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });
    }
  }

  return {
    AVAILABLE_ICONS,
    CLUSTER_LAYER_IDS,
    HEATMAP_LAYER_ID,
    addHeatmapLayer,
    getSourcesLayers,
    loadIcons,
    addClusteringLayers,
    handleMouseEvents,
    handleClickEvents,
  };
}

export function getMapColumns(data, columns, alias_to_column_mapping) {
  let map_columns = columns.map(col => col.name).filter(col => !['integer', 'numeric', 'float'].includes(alias_to_column_mapping[col]?.field_type));
  const invalid_columns = new Set();
  for (const row of data) {
    for (const col of map_columns) {
      const row_value = String(row[col] || '').split(',').map(v => v.trim());
      if (row_value.length === 2) {
        const [lat, lng] = row_value;
        if (Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) {
          invalid_columns.add(col);
        }
      }
      else if (row_value.length !== 0) {
        invalid_columns.add(col);
      }
    }
  }
  map_columns = map_columns.filter(col => !invalid_columns.has(col));
  const non_coordinate_columns = columns.filter(col => !map_columns.includes(col.name)).map(col => alias_to_column_mapping[col.name]).filter(Boolean);
  return {
    coordinate_columns: map_columns.map(col => alias_to_column_mapping[col]).filter(Boolean),
    value_columns: non_coordinate_columns.filter(col => ['integer', 'numeric', 'float'].includes(col?.field_type)),
    columns: non_coordinate_columns,
    all_columns: columns.map(col => alias_to_column_mapping[col.name]).filter(Boolean),
  };
}

function getMinMaxValues(config, data) {
  const { parseSafeNumber } = useBiTableHelper();
  const { start_range_at = 0, end_range_at = 1, start_range_value = 'min', end_range_value = 'max' } = (config.heatmap_options || {});
  const values = data.map(item => item[config.feature_value]).map(parseSafeNumber).filter(num => !Number.isNaN(num));
  let min_value = start_range_value;
  let max_value = end_range_value;
  if (start_range_at === 'min') {
    min_value = Math.min(...values);
  }
  if (end_range_at === 'max') {
    max_value = Math.max(...values);
  }
  return { min_value, max_value };
}

export function getProcessedGeoJSON(data, config) {
  if (!data?.length || !config.value.coordinates || !config.value.feature_value) {
    return { type: 'FeatureCollection', features: [] };
  }
  const { checkRuleMatch, getColorForValue } = useTableConditionalFormatter();
  const { getMinMaxForRule } = useBiTableHelper();
  const min_max_map = {};
  const features = data.reduce((acc, item) => {
    let coordinates = item[config.value.coordinates];
    if (Array.isArray(coordinates)) {
      coordinates = coordinates.map(coord => Number(coord));
    }
    else if (typeof coordinates === 'string') {
      coordinates = coordinates.split(',').map(coord => Number(coord));
    }
    const value = item[config.value.feature_value];
    const { min_value, max_value } = getMinMaxValues(config.value, data);
    const heatmap_value = (value - min_value) / (max_value - min_value);
    let { feature_color, feature_icon, feature_point_size, feature_icon_size } = config.value;
    if (coordinates && value) {
      for (const rule of config.value.conditional_formatting) {
        if (rule.formatting_style === 'single_color') {
          if (checkRuleMatch(rule, item, { field: rule.field }, String(item[rule.field]), config.value.column_alias_mapping)) {
            feature_color = rule.color;
            if (rule.icon) {
              feature_icon = rule.icon;
            }
            break;
          }
        }
        else if (config.value.feature_style === 'color') {
          const { min, max, cell_value } = getMinMaxForRule(rule, data, value, { field: rule.field }, item, min_max_map);
          if (Number.isNaN(cell_value) || (cell_value < min || cell_value > max))
            continue;
          const color = getColorForValue(cell_value, min, max, rule.color, rule.color_range);
          feature_color = color;
          break;
        }
      }
      acc.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates,
        },
        properties: {
          ...item,
          display_value: Number.isNaN(Number(value)) ? Number((value || '0').replace(/,/g, '')) : Number(value),
          feature_color,
          feature_point_size,
          feature_icon,
          feature_icon_size,

          heatmap_value,

          ...config.value.properties.reduce((acc, prop) => {
            acc[prop] = item[prop];
            return acc;
          }, {}),
        },
      });
    }
    return acc;
  }, []);

  return { type: 'FeatureCollection', features };
}
